import {
  ApolloClient,
  ApolloProvider,
  from,
  InMemoryCache,
} from "@apollo/client";
import { Box, MantineProvider, Stack } from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useRecoilState } from "recoil";
import "./App.scss";
import { userInfoAtom } from "./atoms";
import Footer from "./components/Footer";
import BrandNavBar from "./components/NavBar";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import { environment } from "./constants/environment";
import { mantineTheme } from "./mantineTheme";
import NotFoundPage from "./pages/404NotFoundPageTODO/NotFoundPage";
import PrivacyPolicyPage from "./pages/AboutPages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/AboutPages/TermsOfServicePage";
import EmbeddedSandbox from "./pages/ADMINPages/EmbeddedSandbox";
import CollabsPage from "./pages/CollabsPage/CollabsPage";
import DiscoverCreatorsPage from "./pages/DiscoverPage/DiscoverPage";
import { default as RoleSelectionPage } from "./pages/LandingPage/LandingPage";
import ChangePasswordPage from "./pages/LoginFlow/ForgotPasswordPage/ChangePasswordPage/ChangePasswordPage";
import ForgotPasswordPage from "./pages/LoginFlow/ForgotPasswordPage/ForgotPasswordPage";
import LoginPage from "./pages/LoginFlow/LoginPage/LoginPage";
import SignupCompletePage from "./pages/LoginFlow/SignupPage/SignupCompletePage";
import SignupPage from "./pages/LoginFlow/SignupPage/SignupPage";
import VerifyEmailPage from "./pages/LoginFlow/VerifyEmailPage/VerifyEmailPage";
import PasscodePage from "./pages/PasscodePage/PasscodePage";
import ProductsPage from "./pages/ProductsPage/ProductsPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import TikTokPage from "./pages/TikTok/TikTokPage";
import { authLink, errorLink, httpLink } from "./utils/apollo/links";
import { decodeJWTFromCookie, isLoggedIn } from "./utils/jwtUtils";

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([errorLink, authLink, httpLink]),
});

function App() {
  // Recoil State
  const [userInfo, setUserInfo] = useRecoilState(userInfoAtom);

  // TODO: re-enable when enabling login flow again
  useEffect(() => {
    if (isLoggedIn()) {
      setUserInfo(decodeJWTFromCookie());
    } else {
      setUserInfo(null);
    }
  }, []);

  function handleToggleChange(checked: boolean) {
    if (checked) {
      setUserInfo({
        userType: "BRAND_USER",
        brandId: "test", // Set a default brand ID for testing
        email: userInfo?.email!,
      });
    } else {
      setUserInfo({
        userType: "CREATOR",
        brandId: undefined,
        email: userInfo?.email!,
      });
    }
  }

  return (
    <ApolloProvider client={client}>
      <MantineProvider theme={mantineTheme}>
        <Notifications />
        <BrowserRouter>
          <Stack mih="100vh" gap={"0"} bg={"gray.0"}>
            <BrandNavBar />

            {/* Role Toggle */}
            {/* <Box
              bg="white"
              py={"sm"}
              style={{ borderBottom: "1px solid #e9ecef" }}
            >
              <Container size="xl">
                <Group justify="center">
                  <Text
                    size="sm"
                    fw={500}
                    c={!isBrand ? "secondary" : "dimmed"}
                  >
                    Creator
                  </Text>
                  <Switch
                    size="md"
                    checked={isBrand.isBrand}
                    onChange={(event) =>
                      handleToggleChange(event.currentTarget.checked)
                    }
                    color="primary"
                    thumbIcon={
                      isBrand ? (
                        <Text size="xs" fw={700}>
                          B
                        </Text>
                      ) : (
                        <Text size="xs" fw={700}>
                          C
                        </Text>
                      )
                    }
                  />
                  <Text
                    size="sm"
                    fw={500}
                    c={isBrand ? "primary" : "dimmed"}
                  >
                    Brand
                  </Text>
                </Group>
              </Container>
            </Box> */}

            <Box component="main">
              <Routes>
                {/* Passcode page is publicly accessible */}
                <Route path="/passcode" element={<PasscodePage />} />

                {/* All other routes are protected */}
                <Route path="/" element={<RoleSelectionPage />} />

                <Route
                  path="/discover"
                  element={
                    <ProtectedRoute>
                      <DiscoverCreatorsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/products"
                  element={
                    <ProtectedRoute>
                      <ProductsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/collabs"
                  element={
                    <ProtectedRoute>
                      <CollabsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile/:id"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/login">
                  <Route
                    index
                    element={
                      <ProtectedRoute>
                        <LoginPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="change-password"
                    element={<ChangePasswordPage />}
                  />
                </Route>

                <Route path="/signup">
                  <Route
                    index
                    element={
                      <ProtectedRoute>
                        <SignupPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="complete"
                    element={
                      <ProtectedRoute>
                        <SignupCompletePage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                <Route path="tiktok" element={<TikTokPage />} />

                <Route path="/confirm-email" element={<VerifyEmailPage />} />

                {/* Text Pages */}
                <Route path="/privacy" element={<PrivacyPolicyPage />} />

                <Route path="/terms" element={<TermsOfServicePage />} />

                {environment.MODE !== "production" && (
                  <Route path="graphql" element={<EmbeddedSandbox />} />
                )}

                <Route path="*" element={<NotFoundPage />} />

                {/* <Route index element={<RoleSelectionPage />} />
                <Route path="/discover" element={<DiscoverCreatorsPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/collabs" element={<CollabsPage />} />
                <Route path="/brand" element={<BrandCardExample />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/test" element={<CreatorCardExample />} />
                <Route path="/login" element={<LoginPage />} />

                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />

                <Route path="*" element={<NotFoundPage />} /> */}
              </Routes>
            </Box>

            <Footer />
          </Stack>
        </BrowserRouter>
      </MantineProvider>
    </ApolloProvider>
  );
}

export default App;
